import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import UserEntityMemberInvitation, { UserMemberInvitationNotFoundError } from "../user-entity-member-invitation";
import CreateUserEntityMemberInvitationAPIRequest from "./commands/create-user-entity-member-invitation-api-command";
import Session from "users/__session/session";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import UserEntityMemberInvitationSchema from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";

export default class UserEntityMemberInvitationAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json-patch+json" });
  }

  constructor(session: Readonly<Session>) {
    if (session.authToken) {
      this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
    }
  }

  async getUserEntityMemberInvitationById(
    id: Guid,
    abortController?: AbortController,
  ): Promise<UserEntityMemberInvitation> {
    const url = new URL(`/legal-entities/user-entity-member-invitations/${id.value}`, AttorneyHubAPIService.apiBaseUrl);

    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      const params = UserEntityMemberInvitationSchema.deserialize(response.data);
      return new UserEntityMemberInvitation(params);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new UserMemberInvitationNotFoundError(id);
      if (error instanceof CanceledError) throw error;
      throw new UserEntityMemberInvitationAPIServiceError("getUserEntityMemberInvitationById", error);
    }
  }

  async createUserEntityMemberInvitation(invitation: UserEntityMemberInvitation): Promise<void> {
    const url = new URL(
      `/legal-entities/${invitation.userEntityInfo.entityId.value}/user-entity-member-invitations`,
      AttorneyHubAPIService.apiBaseUrl,
    );

    try {
      const payload = new CreateUserEntityMemberInvitationAPIRequest(invitation);
      await axios.post(url.toString(), payload, {
        headers: this.headersWithJson(),
      });
    } catch (error) {
      console.error(error);
      throw new UserEntityMemberInvitationAPIServiceError("createUserEntityMemberInvitation", error, invitation);
    }
  }

  async acceptUserEntityMemberInvitation(invitation: UserEntityMemberInvitation, session: Session): Promise<void> {
    if (!invitation.id) throw new Error("Invitation ID is required to accept an invitation.");
    if (!invitation.registrationInfo?.password) throw new Error("Password is required to accept an invitation.");

    const url = new URL(
      `/legal-entities/user-member-invitations/${invitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );

    try {
      const response = await axios.post(
        url.toString(),
        new AcceptEntityUserInvitationAPIRequest(
          invitation.registrationInfo.password,
          invitation.registrationInfo.name,
        ),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );

      const authResponse = new AuthInfo(AuthInfoSchema.deserialize(response.data)).toAuthResponse();

      session.initializeFromAuthResponse(authResponse);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidRegistrationInfoError(error.response.data);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new UserMemberInvitationNotFoundError(invitation.id);
      throw new UserEntityMemberInvitationAPIServiceError("acceptUserEntityMemberInvitation", error, invitation);
    }
  }
}

export class UserEntityMemberInvitationAPIServiceError extends Error {
  invitation?: UserEntityMemberInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: UserEntityMemberInvitation) {
    super(`UserEntityMemberInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}
